import { NgClass, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GeneralQuery } from '../../client/state';
import { animateText } from '../../core/animations';
import { AuthService } from '../../core/authentication/auth.service';
import { SparrowIconEnum } from '../../core/icons-registry';
import { LangToIcon, SupportedLanguagesType } from '../../core/localization/localization';
import { changeLanguage } from '../../core/utils';

export interface SiteLinkInfo {
  url: string;
  label: string;
  icon: string;
  isVisible: boolean;
}

export interface LocLinkInfo {
  lang: SupportedLanguagesType;
  icon: (typeof LangToIcon)[SupportedLanguagesType];
  label: string;
  langLabel: string;
}

@Component({
  selector: 'sparrow-client-navbar',
  templateUrl: './client-navbar.component.html',
  styleUrls: ['./client-navbar.component.scss'],
  animations: [animateText],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, RouterLinkActive, NgClass, RouterLink, UpperCasePipe],
})
export class ClientNavbarComponent implements OnInit, OnDestroy {
  @Input() sideNavState: boolean;

  selectedLanAbbr: string;
  selectedLanLabel: string;
  isUserManager: boolean;
  langDropdown = false;
  siteLinks: SiteLinkInfo[];
  localizationLinks: LocLinkInfo[] = [
    {
      lang: 'en',
      icon: LangToIcon.en,
      langLabel: 'EN',
      label: 'English',
    },
    {
      lang: 'de',
      icon: LangToIcon.de,
      langLabel: 'DE',
      label: 'Deutsch',
    },
  ];

  readonly SparrowIcon = SparrowIconEnum;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private generalQuery: GeneralQuery,
    private changeDetectorRef: ChangeDetectorRef,
    // `LOCALE_ID` is actually string, but should always conform to our list (except for local development where it is 'en-us')
    @Inject(LOCALE_ID) private locale: SupportedLanguagesType,
  ) {
    this.selectedLanAbbr = this.locale;
    this.selectedLanLabel = this.localizationLinks.find((val) => val.lang === locale).label;
  }

  ngOnInit() {
    combineLatest([
      this.generalQuery.hasSmAccess$,
      this.generalQuery.hasCaAccess$,
      this.generalQuery.hasPlanAccess$,
      this.generalQuery.hasPoolAccess$,
      this.generalQuery.isUserManager$,
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        ([smAccess, caAccess, planAccess, poolAccess, isUserManager]: [
          boolean,
          boolean,
          boolean,
          boolean,
          boolean,
        ]) => {
          this.isUserManager = isUserManager;

          this.siteLinks = [
            {
              url: '/data-cleaning',
              label: $localize`:@@cleanNavLabel:Clean`,
              icon: 'data-cleaning',
              isVisible: caAccess,
            },
            {
              url: '/planning-tool',
              label: $localize`:@@planNavLabel:Plan`,
              icon: 'stock-planning',
              isVisible: planAccess,
            },
            {
              url: '/stock-management',
              label: $localize`:@@stockManagementNavLabel:Stock Management`,
              icon: 'stock-management',
              isVisible: smAccess,
            },
            {
              url: '/pool',
              label: $localize`:@@poolNavLabel:Pool`,
              icon: 'part-pooling',
              isVisible: poolAccess,
            },
          ];
          this.changeDetectorRef.markForCheck();
        },
      );
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  changeLanguage(lang: SupportedLanguagesType) {
    changeLanguage(lang);
  }

  logout() {
    this.authService.logout();
  }

  onLangToggle() {
    this.langDropdown = !this.langDropdown;
  }
}
